/**
 * GitLab Integration API
 *
 * API endpoints for managing GitLab OAuth connections and credentials
 */

import fetchWithAuth from "@/api/secureFetch";

const AGENT_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export interface GitLabCredentialsCheckResponse {
    hasCredentials: boolean;
    gitlabUrl?: string;
    username?: string;
    isConnected?: boolean;
}

export interface InitiateGitLabOAuthRequest {
    userId: number;
    gitlabUrl?: string; // Optional: defaults to gitlab.com
}

export interface InitiateGitLabOAuthResponse {
    authUrl?: string;
    authorizationUrl?: string;
    state: string;
}

export interface GitLabOAuthCallbackParams {
    code: string;
    state: string;
}

export interface GitLabOAuthCallbackResponse {
    success: boolean;
    message?: string;
    error?: string;
    accessToken?: string;
}

/**
 * Check if user has existing GitLab credentials
 */
export async function checkGitLabCredentials(
    userId: number,
    token: string
): Promise<GitLabCredentialsCheckResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/integrations/status?userId=${userId}&vendor=gitlab`,
        token,
        {
            method: "GET"
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to check credentials: ${response.status}`);
    }

    return response.json();
}

/**
 * Initiate OAuth flow for GitLab
 */
export async function initiateGitLabOAuth(
    request: InitiateGitLabOAuthRequest,
    token: string
): Promise<InitiateGitLabOAuthResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/git/gitlab/oauth/authorize`,
        token,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(request)
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to initiate OAuth: ${response.status}`);
    }

    return response.json();
}

/**
 * Handle OAuth callback
 */
export async function handleGitLabOAuthCallback(
    params: GitLabOAuthCallbackParams,
    token: string
): Promise<GitLabOAuthCallbackResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/git/gitlab/oauth/callback?code=${params.code}&state=${params.state}`,
        token,
        {
            method: "GET"
        }
    );

    if (!response.ok) {
        throw new Error(`OAuth callback failed: ${response.status}`);
    }

    return response.json();
}
